import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw } from "lucide-react";

// Live camera (rear camera on phones/tablets, webcam on PC); returns the photo as a File.
export function CameraCapture({ open, onOpenChange, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { return undefined; }
    let cancelled = false;
    setError("");
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: facing, width: { ideal: 1920 } }, audio: false })
      .then((s) => {
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      })
      .catch(() => setError("Kamera tidak bisa dibuka. Izinkan akses kamera di browser, atau gunakan Unggah Foto."));
    if (!navigator.mediaDevices) setError("Perangkat ini tidak mendukung kamera di browser. Gunakan Unggah Foto.");
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  }, [open, facing]);

  const snap = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((b) => {
      if (!b) return;
      onCapture(new File([b], `bukti-${Date.now()}.jpg`, { type: "image/jpeg" }));
      onOpenChange(false);
    }, "image/jpeg", 0.9);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-neutral-200 text-neutral-900" data-testid="camera-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Camera size={18} className="text-crimson" /> Foto Bukti</DialogTitle></DialogHeader>
        {error ? (
          <p data-testid="camera-error" className="rounded-xl bg-amber-50 px-3 py-3 text-sm font-semibold text-amber-700">{error}</p>
        ) : (
          <video ref={videoRef} autoPlay playsInline muted data-testid="camera-video" className="aspect-[3/4] w-full rounded-xl bg-black object-cover sm:aspect-video" />
        )}
        <div className="grid grid-cols-[auto_1fr] gap-2">
          <Button data-testid="camera-switch" variant="outline" onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} className="touch-btn" title="Ganti kamera"><RefreshCw size={16} /></Button>
          <Button data-testid="camera-snap" onClick={snap} disabled={!!error} className="touch-btn bg-crimson text-base font-bold hover:bg-red-500"><Camera size={18} className="mr-2" /> Ambil Foto</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
